import { createContext, use, useSyncExternalStore } from "react";
import type { JSX, ReactNode } from "react";
import { useAuth } from "../auth/AuthContext";
import { ownerKey } from "./ownerKey";

/**
 * The tenant a person works in when they are employed by several tenants (T-105, ADR-0050). The
 * choice applies per session and account; it is sent as header `X-Tenant-Id`.
 */
export interface TenantSelectionStore {
  /**
   * Returns the chosen tenant of an account.
   *
   * @param subject - OIDC subject.
   * @returns The tenant, or `null` if none was chosen.
   */
  get(subject: string): string | null;

  /**
   * Chooses a tenant.
   *
   * @param subject - OIDC subject.
   * @param tenantId - The tenant.
   */
  set(subject: string, tenantId: string): void;

  /**
   * Forgets the choice (switching the tenant, signing out).
   *
   * @param subject - OIDC subject.
   */
  clear(subject: string): void;

  /**
   * Observes changes.
   *
   * @param listener - Called after every change.
   * @returns Stops the observation.
   */
  subscribe(listener: () => void): () => void;
}

const keyPrefix = "timetracking.tenant.";

/**
 * Creates a store in the session storage of the browser; a storage that throws (private mode,
 * blocked) degrades to memory for this page.
 *
 * @param storage - Supplies the session storage.
 * @returns The store.
 */
export function createSessionTenantSelection(storage: () => Storage): TenantSelectionStore {
  const memory = new Map<string, string>();
  const listeners = new Set<() => void>();
  const notify = (): void => {
    listeners.forEach((listener) => {
      listener();
    });
  };
  const store: TenantSelectionStore = {
    get: (subject) => {
      try {
        const stored = storage().getItem(keyPrefix + subject);
        return stored ?? memory.get(subject) ?? null;
      } catch {
        return memory.get(subject) ?? null;
      }
    },
    set: (subject, tenantId) => {
      memory.set(subject, tenantId);

      try {
        storage().setItem(keyPrefix + subject, tenantId);
      } catch {
        // Memory only.
      }

      notify();
    },
    clear: (subject) => {
      memory.delete(subject);

      try {
        storage().removeItem(keyPrefix + subject);
      } catch {
        // Memory only.
      }

      notify();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      const stop = (): void => {
        listeners.delete(listener);
      };
      return stop;
    },
  };

  return store;
}

/**
 * Creates a store in memory (tests).
 *
 * @returns The store.
 */
export function createMemoryTenantSelection(): TenantSelectionStore {
  const values = new Map<string, string>();
  const storage = {
    getItem: (key: string): string | null => values.get(key) ?? null,
    setItem: (key: string, value: string): void => {
      values.set(key, value);
    },
    removeItem: (key: string): void => {
      values.delete(key);
    },
  } as unknown as Storage;
  const store = createSessionTenantSelection(() => storage);

  return store;
}

const TenantSelectionContext = createContext<TenantSelectionStore | null>(null);

/** Properties of {@link TenantSelectionProvider}. */
export interface TenantSelectionProviderProps {
  /** The store. */
  readonly store: TenantSelectionStore;
  /** Child elements. */
  readonly children: ReactNode;
}

/**
 * Provides the tenant selection.
 *
 * @param props - Component properties.
 * @returns The provider.
 */
export function TenantSelectionProvider(props: TenantSelectionProviderProps): JSX.Element {
  const provider = (
    <TenantSelectionContext.Provider value={props.store}>
      {props.children}
    </TenantSelectionContext.Provider>
  );
  return provider;
}

/**
 * Returns the tenant selection store.
 *
 * @returns The store.
 */
export function useTenantSelectionStore(): TenantSelectionStore {
  const store = use(TenantSelectionContext);

  if (store === null) {
    throw new Error("useTenantSelectionStore must be used inside a TenantSelectionProvider.");
  }

  return store;
}

/**
 * Returns the chosen tenant of the signed-in person, updated on changes.
 *
 * @returns The tenant, or `null`.
 */
export function useSelectedTenant(): string | null {
  const store = useTenantSelectionStore();
  const { user } = useAuth();
  const subject = user?.subject ?? null;
  const selected = useSyncExternalStore(
    (listener) => store.subscribe(listener),
    () => (subject === null ? null : store.get(subject)),
  );

  return selected;
}

/**
 * Returns the owner of the local offline data of the signed-in person ({@link ownerKey}).
 *
 * @returns The owner, or `null` if nobody is signed in.
 */
export function useOwnerKey(): string | null {
  const { user } = useAuth();
  const selected = useSelectedTenant();
  const key = user === null ? null : ownerKey(user.subject, selected);

  return key;
}
